import { ThemeableComponent } from '../utils/theme/ThemeableComponent';
import {
    createPopper,
    Instance,
    Modifier,
    ModifierPhases,
    OptionsGeneric,
    VirtualElement,
} from '@popperjs/core';

export abstract class Popper<
    T = {},
    TState = {},
    TThemeKeys = {}
> extends ThemeableComponent<T, TState, TThemeKeys> {
    protected abstract get popperEnabled(): boolean;
    protected popperOptions:
        | Partial<OptionsGeneric<Partial<Modifier<any, any>>>>
        | undefined;
    protected popper: Instance | undefined;

    protected anchorNode: Element | VirtualElement | undefined;

    protected popupNode: HTMLElement | undefined;

    protected arrowNode: HTMLElement | undefined;

    /**
     * A modifer that can be added to the popper options to let the popup node have the same width as the reference node
     *
     * usage:
     * {
     *    placement: 'bottom-start',
     *    modifiers: [
     *        {
     *            ...this.sameWidth,
     *        },
     *    ],
     * };
     *
     * @title sameWidth
     */
    protected sameWidth = {
        name: 'sameWidth',
        enabled: true,
        phase: 'beforeWrite' as ModifierPhases,
        requires: ['computeStyles'],
        fn: ({ state }) => {
            state.styles.popper.width = `${state.rects.reference.width}px`;
        },
        effect: ({ state }) => {
            state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
        },
    };
    private defaultPopperOptions: Partial<
        OptionsGeneric<Partial<Modifier<any, any>>>
    > = {
        placement: 'top',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 5],
                },
            },
        ],
    };

    constructor(props: T) {
        super(props);
    }

    componentDidMount() {
        this.initializePopup();
    }

    componentDidUpdate(prev: T) {
        this.initializePopup();
    }

    componentWillUnmount() {
        if (this.popper) {
            this.popper.destroy();
        }
    }

    protected setAnchorNodeRef = (nodeRef: Element | VirtualElement) => {
        this.anchorNode = nodeRef;
    };
    protected setPopupNodeRef = (nodeRef: HTMLElement) => {
        this.popupNode = nodeRef;
    };
    protected setArrowNodeRef = (nodeRef: HTMLElement) => {
        this.arrowNode = nodeRef;
    };

    protected initializePopup = () => {
        if (this.popperEnabled && this.anchorNode && this.popupNode) {
            const popperOptions = this.getPopperOptions();
            if (popperOptions) {
                this.popper = createPopper(
                    this.anchorNode,
                    this.popupNode,
                    popperOptions
                );
            }
        }
    };

    protected getPopperOptions(): Partial<
        OptionsGeneric<Partial<Modifier<any, any>>>
    > {
        const options = this.popperOptions || this.defaultPopperOptions;
        if (!!this.arrowNode) {
            if (!options.modifiers) {
                options.modifiers = [];
            }
            if (
                !options.modifiers.find(modifier => modifier.name === 'arrow')
            ) {
                options.modifiers.push({
                    name: 'arrow',
                    options: {
                        element: this.arrowNode,
                    },
                });
            }
        }
        return options;
    }

    protected updatePopper() {
        if (this.popper) {
            this.popper.update();
        }
    }
}



// WEBPACK FOOTER //
// ./src/utils/Popper.ts